import * as api from '@api/moashelf/api-function';
import { IMoashelfMenu } from '@datatype/moashelf/type';
import { useApiQuery, useApiMutation, replaceQueryDataItem } from '@hook/common/useApi';
import { IApiResponse } from '@datatype/common/api-type';
import {
  IModifyShelfRequest,
  ISubscriptionData,
  IModifySubscriptionRequest,
  IShelfData,
} from '@datatype/moashelf/api-type';
import { EQUERY_KEY } from '@constant/moashelf/query-key';

/**
 * ###################################
 * @description moashelf query
 * ###################################
 */

/**
 * @description 나의 article item 목록 query 함수
 */
export const useMyShelvesQuery = (mode: IMoashelfMenu['shelf'], page: number, perPage: number) => {
  let queryKey;

  switch (mode) {
    case 'shelf-all':
      queryKey = [EQUERY_KEY.MY_SHELF];
      break;
    case 'shelf-bookmark':
      queryKey = [EQUERY_KEY.MY_SHELF, 'bookmark'];
      break;
    case 'shelf-unread':
      queryKey = [EQUERY_KEY.MY_SHELF, 'unread'];
      break;
    case 'shelf-trash':
      queryKey = [EQUERY_KEY.MY_SHELF, 'trash'];
      break;
    default:
      queryKey = [EQUERY_KEY.MY_SHELF];
  }

  return useApiQuery(queryKey, api.getMyShelves, { page, perPage, mode });
};

/**
 * @description shelf 단건 query 함수
 */
export const useShelfQuery = (shelfKey: string) => {
  return useApiQuery([EQUERY_KEY.MY_SHELF, shelfKey], api.getShelf, shelfKey);
};

/**
 * @description article 단건 query 함수
 */
export const useArticleQuery = (articleKey: string) => {
  return useApiQuery([EQUERY_KEY.ARTICLE, articleKey], api.getArticle, articleKey);
};

/**
 * @description 나의 subscription item 목록 query 함수
 */
export const useMySubscriptionsQuery = (mode: IMoashelfMenu['subscription'], page: number, perPage: number) => {
  let queryKey;

  switch (mode) {
    case 'subscription-all':
      queryKey = [EQUERY_KEY.MY_SUBSCRIPTION];
      break;
    case 'subscription-bookmark':
      queryKey = [EQUERY_KEY.MY_SUBSCRIPTION, 'bookmark'];
      break;
    case 'subscription-trash':
      queryKey = [EQUERY_KEY.MY_SUBSCRIPTION, 'trash'];
      break;
    default:
      queryKey = [EQUERY_KEY.MY_SUBSCRIPTION];
  }

  return useApiQuery(queryKey, api.getMySubscriptions, { page, perPage, mode });
};

/**
 * @description category 목록 query 함수
 */
export const useCategoriesQuery = (page: number, perPage: number) => {
  const queryKey = [EQUERY_KEY.CATEGORIES];
  return useApiQuery(queryKey, api.getCategories, { page, perPage });
};

/**
 * @description provider 목록 query 함수
 */
export const useProvidersQuery = (categoryKey: string | null, page: number, perPage: number) => {
  const queryKey = [EQUERY_KEY.PROVIDERS, categoryKey ?? null];
  return useApiQuery(
    queryKey,
    api.getProviders,
    { page, perPage, categoryKey },
    { enabled: true, staleTime: 0 * 60 * 60 * 1000 },
  );
};

/**
 * @description article 목록 query 함수
 */
export const useArticlesQuery = (categoryKey: string | null, page: number, perPage: number) => {
  const queryKey = [EQUERY_KEY.ARTICLES, categoryKey ?? null];
  return useApiQuery(
    queryKey,
    api.getArticles,
    { page, perPage, categoryKey },
    { enabled: true, staleTime: 0 * 60 * 60 * 1000 },
  );
};

/**
 * ###################################
 * @description moashelf mutation
 * ###################################
 */
export const useModifyShelfMutation = () => {
  return useApiMutation<IApiResponse<IShelfData>, IModifyShelfRequest>(api.modifyShelf, {
    onSuccess: (data) => {
      const itemKey = data.data.key;
      const updatedItem = data.data;

      replaceQueryDataItem([EQUERY_KEY.MY_SHELF], undefined, itemKey, updatedItem);
      replaceQueryDataItem([EQUERY_KEY.MY_SHELF, 'bookmark'], undefined, itemKey, updatedItem);
      replaceQueryDataItem([EQUERY_KEY.MY_SHELF, 'unread'], undefined, itemKey, updatedItem);
      replaceQueryDataItem([EQUERY_KEY.MY_SHELF, 'trash'], undefined, itemKey, updatedItem);
      replaceQueryDataItem([EQUERY_KEY.MY_SHELF, itemKey], undefined, itemKey, updatedItem);
    },
  });
};

export const useModifySubscriptionMutation = () => {
  return useApiMutation<IApiResponse<ISubscriptionData>, IModifySubscriptionRequest>(api.modifySubscription, {
    onSuccess: (data) => {
      const itemKey = data.data.key;
      const updatedItem = data.data;

      replaceQueryDataItem([EQUERY_KEY.MY_SUBSCRIPTION], undefined, itemKey, updatedItem);
      replaceQueryDataItem([EQUERY_KEY.MY_SUBSCRIPTION, 'bookmark'], undefined, itemKey, updatedItem);
      replaceQueryDataItem([EQUERY_KEY.MY_SUBSCRIPTION, 'trash'], undefined, itemKey, updatedItem);
      replaceQueryDataItem([EQUERY_KEY.MY_SUBSCRIPTION, itemKey], undefined, itemKey, updatedItem);
    },
  });
};
